import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { boFetch } from "@/lib/backoffice.server";

const dateRe = /^\d{4}-\d{2}-\d{2}$/;
const timeRe = /^\d{2}:\d{2}$/;

export const getTakenSlots = createServerFn({ method: "POST" })
  .inputValidator((input) => z.object({ date: z.string().regex(dateRe) }).parse(input))
  .handler(async ({ data }) => {
    try {
      const res = await boFetch<{ taken: string[] }>("/api/public/v1/bookings/slots", {
        query: { date: data.date },
      });
      return { taken: res.taken ?? [] };
    } catch (e) {
      console.error("getTakenSlots failed", e);
      return { taken: [] as string[] };
    }
  });

/**
 * Creates the booking in the backoffice and attempts to start a Stripe
 * Checkout session. Stripe Connect onboarding is still pending on the
 * backoffice side, so checkout currently throws a "no disponible" error
 * — the booking is still persisted as "pending" so the despacho can
 * follow up manually.
 */
export const createBookingCheckout = createServerFn({ method: "POST" })
  .inputValidator((input) =>
    z
      .object({
        productId: z.string().min(1).max(200),
        date: z.string().regex(dateRe),
        start: z.string().regex(timeRe),
        end: z.string().regex(timeRe),
        fullName: z.string().trim().min(2).max(120),
        email: z.string().trim().email().max(180),
        phone: z.string().trim().min(6).max(40),
        reason: z.string().trim().min(20).max(2000),
        returnUrl: z.string().url(),
        environment: z.enum(["sandbox", "live"]),
        existingClient: z.boolean().optional(),
        marketingConsent: z.boolean().optional(),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const booking = await boFetch<{ booking: { id: string } }>("/api/public/v1/bookings", {
      method: "POST",
      body: {
        productId: data.productId,
        date: data.date,
        start: data.start,
        end: data.end,
        fullName: data.fullName,
        email: data.email,
        phone: data.phone,
        reason: data.reason,
        existingClient: data.existingClient === true,
        marketingConsent: data.marketingConsent === true,
      },
    });

    // Existing clients with an ongoing case skip payment — booking is
    // persisted and the despacho confirms manually via phone.
    if (data.existingClient) {
      return {
        clientSecret: null as string | null,
        bookingId: booking.booking.id,
        pendingConfirmation: true,
      };
    }

    try {
      const session = await boFetch<{ clientSecret: string }>("/api/public/v1/checkout/session", {
        method: "POST",
        body: {
          type: "booking",
          bookingId: booking.booking.id,
          returnUrl: data.returnUrl,
          environment: data.environment,
        },
      });
      return {
        clientSecret: session.clientSecret as string | null,
        bookingId: booking.booking.id,
        pendingConfirmation: false,
      };
    } catch (e) {
      const err = e as { status?: number; message?: string };
      if (err.status === 501 || err.status === 503) {
        return {
          clientSecret: null as string | null,
          bookingId: booking.booking.id,
          pendingConfirmation: true,
        };
      }
      throw e;
    }
  });


export const getBookingBySession = createServerFn({ method: "POST" })
  .inputValidator((input) => z.object({ sessionId: z.string().min(5).max(200) }).parse(input))
  .handler(async () => {
    // Stripe Connect checkout not yet wired in backoffice → no session lookup.
    return { booking: null as { status?: string } | null };
  });
